import { useSearchParams } from "react-router-dom"

/**
 * Keeps a Weekly Insights page's selected platform pill (Meta Quest / Steam /
 * Apple Vision) in the `platform` URL query param instead of plain component
 * state. Two problems that solves at once:
 *
 * - The page fully unmounts when the user opens an app card and remounts fresh
 *   when they come back from the App Tracking detail page — a plain `useState`
 *   default is lost on that round trip. The URL survives it (browser/back-button
 *   navigation returns to the exact same URL, query string included), so the
 *   platform choice comes back with it.
 * - A direct/shared link can now pre-select a platform, e.g.
 *   `/trending?platform=steam`, the same way a UTM-style link would.
 *
 * `?platform=` is omitted entirely when it matches `defaultPlatform`, so the
 * common case (Meta Quest, the default) keeps a clean URL.
 */
export function useUrlPlatform(defaultPlatform = "meta") {
  const [searchParams, setSearchParams] = useSearchParams()
  const platform = searchParams.get("platform") || defaultPlatform

  const setPlatform = (next) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next && next !== defaultPlatform) params.set("platform", next)
        else params.delete("platform")
        return params
      },
      { replace: true }
    )
  }

  return [platform, setPlatform]
}

export default useUrlPlatform
